import React, { useState } from 'react';
import './auth.css';
import { useAuth } from './AuthContext';
import { api } from './api';

const HANDLE_RE = /^[a-z0-9_]{3,20}$/;

type FormatStatus =
  | { kind: 'empty' }
  | { kind: 'invalid'; reason: string }
  | { kind: 'valid' };

function formatCheck(raw: string): FormatStatus {
  const trimmed = raw.trim().toLowerCase();
  if (!trimmed) return { kind: 'empty' };
  if (!HANDLE_RE.test(trimmed)) {
    return { kind: 'invalid', reason: '3–20 chars, lowercase letters, digits, underscore.' };
  }
  return { kind: 'valid' };
}

const REASON_MESSAGES: Record<string, string> = {
  taken: 'That handle is already taken.',
  reserved: 'That handle is reserved. Pick another.',
  invalid_format: 'That handle has invalid characters.',
};

const HandlePicker: React.FC = () => {
  const { submitHandle, signOut } = useAuth();
  const [handle, setHandle] = useState('');
  const [submitError, setSubmitError] = useState('');
  const [submitting, setSubmitting] = useState(false);

  const formatStatus = formatCheck(handle);

  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (formatStatus.kind !== 'valid') return;
    setSubmitting(true);
    setSubmitError('');
    const trimmed = handle.trim().toLowerCase();
    try {
      const res = await api(`/api/handles/check?handle=${encodeURIComponent(trimmed)}`);
      const data = await res.json();
      if (!data.available) {
        setSubmitError(REASON_MESSAGES[data.reason] || 'Handle is not available.');
        return;
      }
      await submitHandle(handle.trim());
    } catch (err) {
      setSubmitError(err instanceof Error ? err.message : 'Failed to claim handle');
    } finally {
      setSubmitting(false);
    }
  };

  const formatLabel = formatStatus.kind === 'invalid' ? formatStatus.reason : ' ';

  return (
    <div className="auth-container">
      <div className="auth-box">
        <h1>Pick your handle</h1>
        <p className="subtitle">
          This is how friends will find you on the leaderboard. You can change it later.
        </p>
        <form onSubmit={submit}>
          <label htmlFor="handle">Handle</label>
          <input
            id="handle" type="text" required autoComplete="off"
            placeholder="fizzdev"
            value={handle}
            onChange={(e) => { setHandle(e.target.value); setSubmitError(''); }}
          />
          <div className={`handle-status ${formatStatus.kind}`}>{formatLabel}</div>
          <button
            className="primary" type="submit"
            disabled={submitting || formatStatus.kind !== 'valid'}
          >
            {submitting ? 'Claiming…' : 'Claim handle'}
          </button>
        </form>
        {submitError && <div className="auth-message error">{submitError}</div>}
        <div className="auth-footer">
          <button className="link" type="button" onClick={signOut}>Sign out</button>
        </div>
      </div>
    </div>
  );
};

export default HandlePicker;
